import {Shell, type ShellOptions} from './shell-base'
import type {AnagramMessage, EnvironmentConfig, ShellConfig} from './types'

// Small initial placeholder until the iframe reports its content height.
const DefaultHeight = 100
const BufferResetHeight = 0
const VelocitySampleWindowMs = 100
const DecelerationPerMs = 0.997
const MinVelocity = 0.3

function setHeight(element: HTMLElement, value: number): void {
  element.style.height = `${value}px`
}

export class InlineShell extends Shell {
  private readonly scriptElement: HTMLScriptElement
  private container: HTMLDivElement | null = null
  private iframeWrapper!: HTMLDivElement
  private buffer!: HTMLDivElement
  private iframe!: HTMLIFrameElement

  private lastAbsoluteY: number | null = null
  private touchSamples: Array<{time: number; y: number}> = []
  private momentumRafId: number | null = null

  constructor(
    config: EnvironmentConfig,
    experienceId: string,
    scriptElement: HTMLScriptElement,
    options?: ShellOptions
  ) {
    super(config, experienceId, options)
    this.scriptElement = scriptElement
  }

  mount(): void {
    this.container = document.createElement('div')
    this.container.style.position = 'relative'
    this.container.style.width = '100%'
    this.container.style.overflow = 'hidden'

    this.iframeWrapper = document.createElement('div')
    this.iframeWrapper.style.width = '100%'
    this.iframeWrapper.style.transition = 'height 0.2s ease'
    setHeight(this.iframeWrapper, DefaultHeight)

    const iframeUrl = this.buildIframeUrl()
    this.iframe = this.createIframe(iframeUrl)
    this.iframe.setAttribute('loading', 'lazy')
    this.iframe.style.opacity = '0'
    this.iframe.style.transition = 'opacity 0.2s ease'
    this.iframe.setAttribute('scrolling', 'no')

    this.iframeWrapper.appendChild(this.iframe)

    this.buffer = document.createElement('div')
    setHeight(this.buffer, BufferResetHeight)
    this.buffer.style.transition = 'height 0.2s ease'

    this.container.appendChild(this.iframeWrapper)
    this.container.appendChild(this.buffer)

    if (this.scriptElement.parentNode) {
      this.scriptElement.parentNode.insertBefore(this.container, this.scriptElement)
    }

    this.startListeningForIframeMessages()
  }

  override applyPreviewShellConfig(config: ShellConfig): boolean {
    if (config.formFactor !== 'embedded' || !this.container) {
      return false
    }

    this.updateActivePreviewConfig(config)

    const iframeUrl = this.buildIframeUrl()
    this.iframe.src = iframeUrl.toString()
    this.iframe.style.opacity = '0'

    return true
  }

  override destroy(): void {
    if (this.momentumRafId !== null) {
      cancelAnimationFrame(this.momentumRafId)
      this.momentumRafId = null
    }

    this.lastAbsoluteY = null
    this.touchSamples = []
    this.iframe.remove()
    this.container?.remove()
    this.container = null

    super.destroy()
  }

  protected override ownsIframeMessageSource(source: MessageEventSource): boolean {
    return source === this.iframe.contentWindow
  }

  private buildIframeUrl(): URL {
    const iframeUrl = this.createIframeUrl('inline')

    const scriptParams = new URL(this.scriptElement.src).searchParams
    const isFromPreviewScript = scriptParams.get('fromPreview') === 'true'
    const isFromPreview = this.isFromPreview || isFromPreviewScript

    let parentUrl: string | undefined
    if (this.parentUrl !== undefined || this.isFromPreview) {
      parentUrl = this.resolveIframeParentUrl()
    } else {
      const explicitParentUrl = scriptParams.get('parentUrl')
      parentUrl = explicitParentUrl || (isFromPreviewScript ? undefined : window.location.href)
    }
    if (parentUrl) {
      iframeUrl.searchParams.append('parentUrl', parentUrl)
    }
    const pageTitle = document.title
    if (pageTitle && !isFromPreview) {
      iframeUrl.searchParams.append('pageTitle', pageTitle)
    }

    for (const [key, value] of scriptParams.entries()) {
      if (
        key !== 'experienceID' &&
        key !== 'parentUrl' &&
        key !== 'fromPreview' &&
        key !== 'previewConfig' &&
        key !== 'mode'
      ) {
        iframeUrl.searchParams.append(key, value)
      }
    }

    if (!iframeUrl.searchParams.has('mode')) {
      iframeUrl.searchParams.append('mode', 'inline')
    }

    return iframeUrl
  }

  protected onIframeReady(): void {
    this.iframe.style.opacity = '1'
  }

  protected handleShellSpecificIframeMessage(msg: AnagramMessage): void {
    switch (msg.type) {
      case 'ANAGRAM_RESIZE': {
        const newHeight = Number(msg.height)
        if (!Number.isNaN(newHeight) && newHeight > 0) {
          const currentIframeHeight = parseInt(this.iframeWrapper.style.height, 10) || DefaultHeight
          const currentBufferHeight = parseInt(this.buffer.style.height, 10) || BufferResetHeight
          if (msg.allowShrink === true && newHeight < currentIframeHeight) {
            setHeight(this.iframeWrapper, newHeight)
            setHeight(this.buffer, BufferResetHeight)
          }
          if (newHeight > currentIframeHeight) {
            const growthNeeded = newHeight - currentIframeHeight
            if (growthNeeded <= currentBufferHeight) {
              setHeight(this.iframeWrapper, newHeight)
              setHeight(this.buffer, currentBufferHeight - growthNeeded)
            } else {
              setHeight(this.iframeWrapper, newHeight)
              setHeight(this.buffer, BufferResetHeight)
            }
          }
        }
        break
      }

      case 'ANAGRAM_START_OVER': {
        setHeight(this.iframeWrapper, DefaultHeight)
        setHeight(this.buffer, BufferResetHeight)
        break
      }

      case 'ANAGRAM_SCROLL': {
        let scrollDeltaY = msg.deltaY || 0
        const scrollDeltaMode = msg.deltaMode || 0
        if (scrollDeltaMode === 1) scrollDeltaY *= 40
        else if (scrollDeltaMode === 2) scrollDeltaY *= window.innerHeight
        if (scrollDeltaY !== 0) {
          window.scrollBy(0, scrollDeltaY)
        }
        break
      }

      case 'ANAGRAM_TOUCH_START': {
        if (this.momentumRafId !== null) {
          cancelAnimationFrame(this.momentumRafId)
          this.momentumRafId = null
        }
        if (msg.clientY != null) {
          const rect = this.iframe.getBoundingClientRect()
          this.lastAbsoluteY = msg.clientY + rect.top
          this.touchSamples = [{time: performance.now(), y: this.lastAbsoluteY}]
        }
        break
      }

      case 'ANAGRAM_TOUCH_MOVE': {
        if (msg.clientY != null && this.lastAbsoluteY !== null) {
          const rect = this.iframe.getBoundingClientRect()
          const absoluteY = msg.clientY + rect.top
          const deltaY = this.lastAbsoluteY - absoluteY
          this.lastAbsoluteY = absoluteY
          const now = performance.now()

          if (Math.abs(deltaY) < 150) {
            this.touchSamples.push({time: now, y: absoluteY})
            if (deltaY !== 0) {
              window.scrollBy(0, deltaY)
            }
          }

          while (this.touchSamples.length > 1 && now - (this.touchSamples[0]?.time ?? 0) > VelocitySampleWindowMs * 2) {
            this.touchSamples.shift()
          }
        }
        break
      }

      case 'ANAGRAM_TOUCH_END': {
        if (this.lastAbsoluteY !== null) {
          const now = performance.now()
          const recent = this.touchSamples.filter((s) => now - s.time < VelocitySampleWindowMs)

          if (recent.length >= 2) {
            const first = recent.at(0)
            const last = recent.at(-1)
            if (!first || !last) break
            const dt = last.time - first.time

            if (dt >= 10) {
              let velocity = (first.y - last.y) / dt

              if (Math.abs(velocity) > MinVelocity) {
                let lastTime = performance.now()

                const tick = (): void => {
                  const tickNow = performance.now()
                  const elapsed = tickNow - lastTime
                  lastTime = tickNow

                  const delta = velocity * elapsed
                  velocity *= DecelerationPerMs ** elapsed

                  if (Math.abs(velocity) < MinVelocity) {
                    this.momentumRafId = null
                    return
                  }

                  if (Math.abs(delta) > 0.5) {
                    window.scrollBy(0, delta)
                  }

                  this.momentumRafId = requestAnimationFrame(tick)
                }

                this.momentumRafId = requestAnimationFrame(tick)
              }
            }
          }
        }
        this.lastAbsoluteY = null
        break
      }
    }
  }
}
